/**
 * New configurator only: sync gallery so the selected variant image is the first active slide.
 * Loaded exclusively from sections/ee-new-configurator.liquid.
 */
(() => {
  const MAX_ATTEMPTS = 25;
  const RETRY_MS = 100;
  const MAIN_WIDTHS = [246, 493, 600, 713, 823, 990, 1100, 1206, 1346, 1426, 1646, 1946];
  const THUMB_WIDTHS = [54, 74, 104, 162, 208, 324, 416];

  function getProductInfo() {
    return document.querySelector('ee-configurator-product-info[data-section]');
  }

  function getVariants(sectionId) {
    const el = document.getElementById(`ProductJSON-${sectionId}`);
    if (!el) return [];
    try {
      const data = JSON.parse(el.textContent || '[]');
      return Array.isArray(data) ? data : [];
    } catch (_) {
      return [];
    }
  }

  function getFallbackVariant(sectionId, variants) {
    const productInfo = getProductInfo();
    const urlVariantId = new URLSearchParams(window.location.search).get('variant');

    if (urlVariantId && variants.length) {
      const fromUrl = variants.find((variant) => String(variant.id) === String(urlVariantId));
      if (fromUrl) return fromUrl;
    }

    const selectedEl = productInfo?.querySelector('[data-selected-variant]');
    if (selectedEl) {
      try {
        const initial = JSON.parse(selectedEl.textContent || '{}');
        if (initial?.id) return initial;
      } catch (_) {}
    }

    return variants.find((variant) => variant.available) || variants[0] || null;
  }

  function getSelectedVariant(sectionId) {
    const productInfo = getProductInfo();
    const variantSelects = productInfo?.querySelector('ee-variant-selects');
    const variants = getVariants(sectionId);
    if (!variants.length) return null;

    const urlVariantId = new URLSearchParams(window.location.search).get('variant');
    if (urlVariantId) {
      const fromUrl = variants.find((variant) => String(variant.id) === String(urlVariantId));
      if (fromUrl) return fromUrl;
    }

    if (!variantSelects) return getFallbackVariant(sectionId, variants);

    const selectedOptions = [];

    variantSelects.querySelectorAll('select').forEach((select) => {
      if (select.value) selectedOptions.push(select.value);
    });

    variantSelects.querySelectorAll('fieldset').forEach((fieldset) => {
      const checked = fieldset.querySelector('input[type="radio"]:checked');
      if (checked?.value) selectedOptions.push(checked.value);
    });

    if (selectedOptions.length) {
      const matched = variants.find((variant) =>
        variant.options.every((option, index) => selectedOptions[index] === option)
      );
      if (matched) return matched;
    }

    return getFallbackVariant(sectionId, variants);
  }

  function getVariantTerms(variant) {
    return [variant.option1, variant.option2, variant.option3]
      .filter(Boolean)
      .map((value) => String(value).trim())
      .filter(Boolean);
  }

  function getVariantMediaSources(variant) {
    const featuredMedia = variant?.featured_media;
    if (!featuredMedia?.id) return null;

    const preview = featuredMedia.preview_image || {};
    const mainSrc = preview.src || featuredMedia.src || variant.featured_image?.src || null;
    if (!mainSrc) return null;

    const baseSrc = mainSrc.split('?')[0];

    return {
      featuredMedia,
      baseSrc,
      alt: featuredMedia.alt || variant.title || '',
      aspectRatio: preview.aspect_ratio || featuredMedia.aspect_ratio || 1,
      width: preview.width || featuredMedia.width || 1500,
      height: preview.height || featuredMedia.height || 1500,
    };
  }

  function buildSrcset(baseSrc, widths) {
    return widths.map((width) => `${baseSrc}?width=${width} ${width}w`).join(', ');
  }

  function getViewerSlideTemplate(galleryList) {
    return (
      galleryList.querySelector(
        'li.product__media-item:not([data-add-on-media-id]):not([data-open-image]) .product-media-container.media-type-image'
      )?.closest('li.product__media-item') ||
      galleryList.querySelector(
        'li.product__media-item:not([data-add-on-media-id]):not([data-open-image]) .product-media-container'
      )?.closest('li.product__media-item')
    );
  }

  function getThumbnailTemplate(thumbnailList) {
    return (
      thumbnailList.querySelector('li.thumbnail-list__item[data-target]:not([hidden])') ||
      thumbnailList.querySelector('li.thumbnail-list__item[data-target]')
    );
  }

  function createViewerSlideFromTemplate(templateSlide, sectionId, media) {
    const slide = templateSlide.cloneNode(true);
    const mediaId = `${sectionId}-${media.featuredMedia.id}`;

    slide.id = `Slide-${sectionId}-${media.featuredMedia.id}`;
    slide.className = templateSlide.className.replace(/\bis-active\b/g, '').trim();
    slide.dataset.mediaId = mediaId;
    slide.dataset.alt = media.alt.toLowerCase();
    slide.hidden = false;
    slide.removeAttribute('hidden');

    slide.querySelectorAll('deferred-media, product-model, .product__xr-button').forEach((node) => {
      node.remove();
    });

    const container = slide.querySelector('.product-media-container');
    if (container) {
      container.style.setProperty('--ratio', media.aspectRatio);
      container.style.setProperty('--preview-ratio', media.aspectRatio);
    }

    const modalOpener = slide.querySelector('modal-opener.product__modal-opener');
    if (modalOpener) {
      modalOpener.dataset.modal = `#ProductModal-${sectionId}`;
    }

    const mediaIcon = slide.querySelector('.product__media-icon');
    const templateMediaIcon = templateSlide.querySelector('.product__media-icon');
    if (mediaIcon && templateMediaIcon) {
      templateMediaIcon.classList.forEach((cls) => {
        if (cls.startsWith('product__media-icon--')) {
          mediaIcon.classList.add(cls);
        }
      });
    }

    const img = slide.querySelector('.product__media img');
    if (img) {
      img.src = `${media.baseSrc}?width=1946`;
      img.srcset = buildSrcset(media.baseSrc, MAIN_WIDTHS);
      img.alt = media.alt;
      img.loading = 'lazy';
      img.width = media.width;
      img.height = media.height;
      if (!img.classList.contains('image-magnify-hover') && !img.classList.contains('image-magnify-lightbox')) {
        const templateImg = templateSlide.querySelector('.product__media img');
        if (templateImg?.className) img.className = templateImg.className;
      }
    }

    const toggleBtn = slide.querySelector('.product__media-toggle');
    if (toggleBtn) {
      toggleBtn.dataset.mediaId = String(media.featuredMedia.id);
      const templateToggle = templateSlide.querySelector('.product__media-toggle');
      if (templateToggle?.className) {
        toggleBtn.className = templateToggle.className;
      }
    }

    return slide;
  }

  function createThumbnailFromTemplate(templateThumb, sectionId, media, thumbIndex) {
    const thumb = templateThumb.cloneNode(true);
    const mediaId = `${sectionId}-${media.featuredMedia.id}`;
    const thumbnailId = `Thumbnail-${sectionId}-variant-${media.featuredMedia.id}`;

    thumb.id = `Slide-Thumbnails-${sectionId}-variant-${media.featuredMedia.id}`;
    thumb.className = templateThumb.className.replace(/\bhidden\b/g, '').trim();
    thumb.dataset.target = mediaId;
    thumb.dataset.mediaPosition = String(thumbIndex + 1);
    thumb.hidden = false;
    thumb.removeAttribute('hidden');

    thumb.querySelectorAll('.thumbnail__badge').forEach((badge) => badge.remove());

    const button = thumb.querySelector('button.thumbnail');
    const templateButton = templateThumb.querySelector('button.thumbnail');
    const templateImg = templateThumb.querySelector('img');

    if (button) {
      if (templateButton?.className) button.className = templateButton.className;
      button.setAttribute('aria-label', templateButton?.getAttribute('aria-label') || `Load image ${thumbIndex + 1}`);
      button.setAttribute('aria-controls', `GalleryViewer-${sectionId}`);
      button.setAttribute('aria-describedby', thumbnailId);
      button.removeAttribute('aria-current');
    }

    const img = thumb.querySelector('img');
    if (img) {
      img.id = thumbnailId;
      img.src = `${media.baseSrc}?width=416`;
      img.srcset = buildSrcset(media.baseSrc, THUMB_WIDTHS);
      img.alt = media.alt;
      img.loading = 'lazy';
      img.width = 416;
      img.height = 416;
      if (templateImg?.sizes) img.sizes = templateImg.sizes;
    }

    return thumb;
  }

  function bindThumbnailClick(thumbnailItem, mediaGallery) {
    const button = thumbnailItem?.querySelector('button');
    const target = thumbnailItem?.dataset?.target;
    if (!button || !target || button.dataset.variantGalleryBound === 'true') return;

    button.dataset.variantGalleryBound = 'true';
    button.addEventListener('click', () => {
      mediaGallery?.setActiveMedia?.(target, false);
    });
  }

  function activateViewerSlide(galleryList, galleryViewer, slide) {
    slide.hidden = false;
    slide.removeAttribute('hidden');

    if (galleryList.firstElementChild !== slide) {
      galleryList.prepend(slide);
    }

    galleryList.querySelectorAll('li[data-media-id]').forEach((item) => {
      if (item.hasAttribute('data-add-on-media-id') || item.hasAttribute('data-open-image')) return;
      item.classList.toggle('is-active', item === slide);
    });

    galleryViewer?.resetPages?.();
    galleryList.scrollTo({ left: 0, behavior: 'instant' in window ? 'instant' : 'auto' });
  }

  function activateThumbnailSlide(thumbnailList, thumbnailsComponent, thumb, mediaGallery) {
    thumb.hidden = false;
    thumb.removeAttribute('hidden');

    if (thumbnailList.firstElementChild !== thumb) {
      thumbnailList.prepend(thumb);
    }

    thumbnailList.querySelectorAll('li[data-target] button').forEach((button) => {
      button.removeAttribute('aria-current');
    });
    thumb.querySelector('button')?.setAttribute('aria-current', 'true');

    bindThumbnailClick(thumb, mediaGallery);
    thumbnailsComponent?.resetPages?.();
    thumbnailList.scrollTo({ left: 0, behavior: 'instant' in window ? 'instant' : 'auto' });
  }

  function ensureViewerSlide(sectionId, variant) {
    const media = getVariantMediaSources(variant);
    if (!media) return null;

    const mediaId = `${sectionId}-${media.featuredMedia.id}`;
    const galleryViewer = document.getElementById(`GalleryViewer-${sectionId}`);
    const galleryList = document.getElementById(`Slider-Gallery-${sectionId}`);
    if (!galleryViewer || !galleryList) return null;

    let slide = galleryList.querySelector(
      `li[data-media-id="${mediaId}"]:not([data-add-on-media-id])`
    );

    if (!slide) {
      const templateSlide = getViewerSlideTemplate(galleryList);
      if (!templateSlide) return null;
      slide = createViewerSlideFromTemplate(templateSlide, sectionId, media);
      galleryList.appendChild(slide);
    }

    activateViewerSlide(galleryList, galleryViewer, slide);
    return slide;
  }

  function ensureThumbnailSlide(sectionId, variant, mediaGallery) {
    const media = getVariantMediaSources(variant);
    if (!media) return null;

    const mediaId = `${sectionId}-${media.featuredMedia.id}`;
    const thumbnailsComponent = document.getElementById(`GalleryThumbnails-${sectionId}`);
    const thumbnailList = document.getElementById(`Slider-Thumbnails-${sectionId}`);
    if (!thumbnailList) return null;

    let thumb = thumbnailList.querySelector(`li[data-target="${mediaId}"]`);

    if (!thumb) {
      const templateThumb = getThumbnailTemplate(thumbnailList);
      if (!templateThumb) return null;
      const thumbIndex = thumbnailList.querySelectorAll('li[data-target]').length;
      thumb = createThumbnailFromTemplate(templateThumb, sectionId, media, thumbIndex);
      thumbnailList.appendChild(thumb);
    }

    activateThumbnailSlide(thumbnailList, thumbnailsComponent, thumb, mediaGallery);
    return thumb;
  }

  function syncVariantGallery(sectionId, variant) {
    if (!variant?.featured_media?.id) return false;

    const mediaGallery = document.getElementById(`MediaGallery-${sectionId}`);
    if (!mediaGallery?.setActiveMedia) return false;

    const mediaId = `${sectionId}-${variant.featured_media.id}`;

    mediaGallery.filterMediaByAlt?.(getVariantTerms(variant));

    const viewerSlide = ensureViewerSlide(sectionId, variant);
    ensureThumbnailSlide(sectionId, variant, mediaGallery);

    if (!viewerSlide) return false;

    mediaGallery.setActiveMedia(mediaId, true);

    const galleryList = document.getElementById(`Slider-Gallery-${sectionId}`);
    const thumbnailList = document.getElementById(`Slider-Thumbnails-${sectionId}`);
    const viewer = document.getElementById(`GalleryViewer-${sectionId}`);
    const thumbnailsComponent = document.getElementById(`GalleryThumbnails-${sectionId}`);
    const activeViewerSlide = galleryList?.querySelector(
      `li[data-media-id="${mediaId}"]:not([data-add-on-media-id])`
    );
    const activeThumbSlide = thumbnailList?.querySelector(`li[data-target="${mediaId}"]`);

    if (activeViewerSlide && galleryList?.firstElementChild !== activeViewerSlide) {
      galleryList.prepend(activeViewerSlide);
    }
    if (activeThumbSlide && thumbnailList?.firstElementChild !== activeThumbSlide) {
      thumbnailList.prepend(activeThumbSlide);
    }

    galleryList?.scrollTo({ left: 0, behavior: 'instant' in window ? 'instant' : 'auto' });
    thumbnailList?.scrollTo({ left: 0, behavior: 'instant' in window ? 'instant' : 'auto' });
    viewer?.resetPages?.();
    thumbnailsComponent?.resetPages?.();

    const openMode =
      window.EeConfiguratorGallery?.isOpenScrollMode?.() ?? window.scrollY > 200;
    window.EeConfiguratorGallery?.updateImageVisibility?.(openMode);
    window.EeConfiguratorGallery?.requestMobileStackSync?.();

    return true;
  }

  function initVariantGallery(sectionId, attempt = 0) {
    const variant = getSelectedVariant(sectionId);
    if (!variant) return;

    if (syncVariantGallery(sectionId, variant)) return;
    if (attempt < MAX_ATTEMPTS) {
      setTimeout(() => initVariantGallery(sectionId, attempt + 1), RETRY_MS);
    }
  }

  function bindVariantGalleryEvents(sectionId) {
    const productInfo = getProductInfo();
    const variantSelects = productInfo?.querySelector('ee-variant-selects');
    if (!productInfo) return;

    const refresh = (variant) => {
      syncVariantGallery(sectionId, variant || getSelectedVariant(sectionId));
    };

    variantSelects?.addEventListener('ee-variant-change', () => {
      setTimeout(() => refresh(), 0);
    });

    productInfo.addEventListener('configurator:variant-changed', (event) => {
      refresh(event.detail?.variant);
    });
  }

  function boot() {
    const productInfo = getProductInfo();
    if (!productInfo?.dataset.section) return;

    const sectionId = productInfo.dataset.section;
    bindVariantGalleryEvents(sectionId);
    initVariantGallery(sectionId);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
